// File: frontend/src/components/seo-analytics/AhrefsOverviewTab.jsx
// Version: 1.1
// Date: 2026-04-27
// Description: Ahrefs Overview tab — 4 gradient hero KPI cards (DR, Traffic,
//              Keywords, Top 3) + 4 white sub-cards (Backlinks, Ref Domains,
//              AI Citations, Last Snapshot). Inline-styled to match the Voice
//              page editorial look. Includes a Creator-only "Refresh from
//              Ahrefs" trigger and a collapsible raw_payload accordion for
//              debugging.
// History:
// - [v1.1]: Light editorial restyle — gradient hero cards (4 across) +
//           white sub-cards. Lucide icons. No data flow changes.
// - [v1.0]: Initial dark KPI grid.

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  BarChart3,
  Search,
  Trophy,
  Link2,
  Network,
  Sparkles,
  Clock,
} from 'lucide-react';
import seoAnalyticsService from '../../services/seoAnalyticsService';
import { useAuth } from '../../context/AuthContext';

const CREATOR_EMAIL = 'afitbk@gmail.com';

// Mirror of AdminInstanceProfile's Creator detection — defense-in-depth.
const isCreator = (user) => {
  if (!user) return false;
  if (user.email === CREATOR_EMAIL) return true;
  if (Array.isArray(user.roles)) {
    return user.roles.some((r) => {
      const name = (typeof r === 'string' ? r : r?.name) || '';
      return name.toUpperCase() === 'CREATOR';
    });
  }
  if (typeof user.role === 'string') return user.role.toUpperCase() === 'CREATOR';
  return false;
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtMoney = (n) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

// Compute the simple percent delta from a 7-day-ago point in trends.
// Returns: { delta: number|null, pct: number|null, dir: 'up'|'down'|'flat'|null }
const computeDelta = (current, prior) => {
  if (current == null || prior == null || prior === 0) {
    return { delta: null, pct: null, dir: null };
  }
  const delta = current - prior;
  const pct = (delta / prior) * 100;
  const dir = Math.abs(pct) < 0.5 ? 'flat' : delta > 0 ? 'up' : 'down';
  return { delta, pct, dir };
};

// ---------- Inline style helpers (mirrors VoiceDashboard.js patterns) ----------

const gradientCardStyle = (startColor, endColor) => ({
  border: 'none',
  background: `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`,
  color: 'white',
  boxShadow:
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '0.75rem',
  padding: '1.25rem 1.5rem',
});

const iconBgStyle = {
  backgroundColor: 'rgba(255,255,255,0.2)',
  borderRadius: '0.75rem',
  padding: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const whiteCardStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.75rem',
  padding: '1.25rem 1.5rem',
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const subCardIconStyle = (tint) => ({
  backgroundColor: tint,
  borderRadius: '0.5rem',
  padding: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ---------- Delta indicator ----------

const DeltaIndicator = ({ dir, pct, invertColor = false, lightTheme = false }) => {
  if (dir == null || pct == null) {
    return (
      <span
        style={{
          fontSize: '0.78rem',
          fontWeight: 600,
          color: lightTheme ? '#94a3b8' : 'rgba(255,255,255,0.7)',
        }}
      >
        —
      </span>
    );
  }
  if (dir === 'flat') {
    return (
      <span
        style={{
          fontSize: '0.78rem',
          fontWeight: 600,
          color: lightTheme ? '#94a3b8' : 'rgba(255,255,255,0.85)',
        }}
      >
        → 0%
      </span>
    );
  }

  const isGood = invertColor ? dir === 'down' : dir === 'up';
  const color = lightTheme
    ? isGood
      ? '#16a34a'
      : '#dc2626'
    : isGood
    ? 'rgba(220,252,231,1)'
    : 'rgba(254,226,226,1)';
  const arrow = dir === 'up' ? '↗' : '↘';
  const sign = pct > 0 ? '+' : '';
  return (
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color }}>
      {arrow} {sign}
      {pct.toFixed(1)}%
    </span>
  );
};

// ---------- Cards ----------

const HeroCard = ({ start, end, label, value, subline, icon, delta }) => (
  <div style={gradientCardStyle(start, end)}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: '0.5rem',
      }}
    >
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {label}
      </div>
      <div style={iconBgStyle}>{icon}</div>
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
      <div style={{ fontSize: '2.25rem', fontWeight: 'bold', lineHeight: 1.05 }}>{value}</div>
      {delta}
    </div>
    {subline && (
      <p
        style={{
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.75)',
          marginTop: '0.25rem',
          margin: '0.25rem 0 0 0',
        }}
      >
        {subline}
      </p>
    )}
  </div>
);

const SubCard = ({ label, value, subline, icon, iconTint, iconColor, delta }) => (
  <div style={whiteCardStyle}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#64748b',
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </div>
      <div style={subCardIconStyle(iconTint)}>
        {React.cloneElement(icon, { color: iconColor, size: 18 })}
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '1.65rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.05 }}>
        {value}
      </span>
      {delta}
    </div>
    {subline && (
      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{subline}</div>
    )}
  </div>
);

const HeroSkeleton = () => (
  <div
    style={{
      ...gradientCardStyle('#e2e8f0', '#cbd5e1'),
      minHeight: '128px',
    }}
    aria-hidden="true"
  />
);

const SubSkeleton = () => (
  <div style={{ ...whiteCardStyle, minHeight: '110px' }} aria-hidden="true">
    <div className="skeleton-line skeleton-line-sm" />
    <div className="skeleton-line skeleton-line-lg" />
  </div>
);

// ---------- Main ----------

const AhrefsOverviewTab = ({ domainId }) => {
  const { user } = useAuth();
  const userIsCreator = isCreator(user);

  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [citations, setCitations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showPayload, setShowPayload] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!domainId) return;
    setLoading(true);
    setError(null);
    try {
      const [ov, tr, ci] = await Promise.all([
        seoAnalyticsService.getAhrefsOverview(domainId).catch(() => null),
        seoAnalyticsService.getAhrefsTrends(domainId, 14).catch(() => null),
        seoAnalyticsService.getAhrefsCitations(domainId).catch(() => null),
      ]);
      setOverview(ov);
      setTrends(tr);
      setCitations(ci);
      if (!ov) setError('Ahrefs snapshot unavailable for this domain.');
    } catch (err) {
      console.error('[AhrefsOverviewTab] fetch failed:', err);
      setError(err.message || 'Failed to load Ahrefs overview.');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = async () => {
    if (!domainId || refreshing) return;
    setRefreshing(true);
    const tid = toast.loading('Triggering Ahrefs snapshot — this may take ~30 seconds…');
    try {
      await seoAnalyticsService.triggerAhrefsSnapshot(domainId);
      toast.success('Snapshot collected', { id: tid });
      await fetchAll();
    } catch (err) {
      console.error('[AhrefsOverviewTab] snapshot trigger failed:', err);
      toast.error(err.message || 'Snapshot failed', { id: tid });
    } finally {
      setRefreshing(false);
    }
  };

  // Build week-over-week comparison from trends data.
  const wow = React.useMemo(() => {
    const empty = {
      organic_traffic: { dir: null, pct: null },
      organic_keywords: { dir: null, pct: null },
      backlinks: { dir: null, pct: null },
      ref_domains: { dir: null, pct: null },
      dr: { dir: null, pct: null },
    };
    if (!trends?.data_points?.length) return empty;
    const points = [...trends.data_points].sort(
      (a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date)
    );
    const latest = points[points.length - 1];
    if (!latest) return empty;
    const latestTime = new Date(latest.snapshot_date).getTime();
    const sevenDaysAgo = latestTime - 7 * 24 * 60 * 60 * 1000;
    let prior = null;
    for (let i = points.length - 2; i >= 0; i -= 1) {
      const t = new Date(points[i].snapshot_date).getTime();
      if (t <= sevenDaysAgo) {
        prior = points[i];
        break;
      }
    }
    if (!prior && points.length > 1) prior = points[0];
    if (!prior) return empty;
    return {
      organic_traffic: computeDelta(latest.organic_traffic, prior.organic_traffic),
      organic_keywords: computeDelta(latest.organic_keywords, prior.organic_keywords),
      backlinks: computeDelta(latest.backlinks, prior.backlinks),
      ref_domains: computeDelta(latest.ref_domains, prior.ref_domains),
      dr: computeDelta(latest.dr, prior.dr),
    };
  }, [trends]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.25rem',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <HeroSkeleton key={`hero-${i}`} />
          ))}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <SubSkeleton key={`sub-${i}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div className="inline-error">{error}</div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div className="empty-state">
          No Ahrefs data yet. Snapshots collect daily at 02:00 UTC.
        </div>
        {userIsCreator && (
          <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="action-btn action-btn-primary"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Trigger an immediate Ahrefs snapshot"
            >
              {refreshing ? 'Collecting…' : 'Refresh from Ahrefs'}
            </button>
          </div>
        )}
      </div>
    );
  }

  const totalCitations = citations
    ? ['aio', 'chatgpt', 'perplexity', 'gemini', 'copilot'].reduce(
        (s, k) => s + (citations[k] || 0),
        0
      )
    : 0;
  const citationsAllNull =
    !citations ||
    ['aio', 'chatgpt', 'perplexity', 'gemini', 'copilot'].every((k) => citations[k] == null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Hero gradient cards (4 across) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.25rem',
        }}
      >
        <HeroCard
          start="#059669"
          end="#047857"
          label="Domain Rating"
          value={overview.dr != null ? overview.dr : '—'}
          subline={`Ahrefs Rank: ${fmtNum(overview.ahrefs_rank)}`}
          icon={<TrendingUp size={20} color="white" />}
          delta={<DeltaIndicator dir={wow.dr.dir} pct={wow.dr.pct} />}
        />
        <HeroCard
          start="#ea580c"
          end="#c2410c"
          label="Organic Traffic"
          value={fmtNum(overview.organic_traffic)}
          subline={`${fmtMoney(overview.traffic_value_usd)}/mo est. value`}
          icon={<BarChart3 size={20} color="white" />}
          delta={
            <DeltaIndicator dir={wow.organic_traffic.dir} pct={wow.organic_traffic.pct} />
          }
        />
        <HeroCard
          start="#2563eb"
          end="#1d4ed8"
          label="Organic Keywords"
          value={fmtNum(overview.organic_keywords)}
          subline={
            overview.top3_count != null
              ? `${fmtNum(overview.top3_count)} ranking in top 3`
              : 'Tracked daily'
          }
          icon={<Search size={20} color="white" />}
          delta={
            <DeltaIndicator dir={wow.organic_keywords.dir} pct={wow.organic_keywords.pct} />
          }
        />
        <HeroCard
          start="#7c3aed"
          end="#6d28d9"
          label="Top 3 Positions"
          value={fmtNum(overview.top3_count)}
          subline="Tracked daily"
          icon={<Trophy size={20} color="white" />}
        />
      </div>

      {/* White sub-cards (4 across) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
        }}
      >
        <SubCard
          label="Total Backlinks"
          value={fmtNum(overview.backlinks)}
          icon={<Link2 />}
          iconTint="#dbeafe"
          iconColor="#2563eb"
          delta={
            <DeltaIndicator
              dir={wow.backlinks.dir}
              pct={wow.backlinks.pct}
              lightTheme
            />
          }
        />
        <SubCard
          label="Referring Domains"
          value={fmtNum(overview.ref_domains)}
          icon={<Network />}
          iconTint="#fef3c7"
          iconColor="#d97706"
          delta={
            <DeltaIndicator
              dir={wow.ref_domains.dir}
              pct={wow.ref_domains.pct}
              lightTheme
            />
          }
        />
        <SubCard
          label="AI Citations"
          value={citationsAllNull ? '—' : fmtNum(totalCitations)}
          subline={citationsAllNull ? 'Brand Radar config required' : 'Across 5 AI surfaces'}
          icon={<Sparkles />}
          iconTint="#ede9fe"
          iconColor="#7c3aed"
        />
        <SubCard
          label="Last Snapshot"
          value={fmtDate(overview.snapshot_date)}
          subline="Auto-collected daily"
          icon={<Clock />}
          iconTint="#dcfce7"
          iconColor="#059669"
        />
      </div>

      <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
        {userIsCreator && (
          <button
            type="button"
            className="action-btn action-btn-primary"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Trigger an immediate Ahrefs snapshot"
          >
            {refreshing ? 'Collecting…' : 'Refresh from Ahrefs'}
          </button>
        )}
        <button
          type="button"
          className="action-btn action-btn-ghost"
          onClick={() => setShowPayload((s) => !s)}
          aria-expanded={showPayload}
          aria-label="Toggle raw snapshot payload"
        >
          {showPayload ? 'Hide raw payload' : 'View raw payload'}
        </button>
      </div>

      {showPayload && (
        <pre className="raw-payload-accordion" aria-label="Snapshot raw payload JSON">
          {JSON.stringify(overview, null, 2)}
        </pre>
      )}

      <div
        style={{
          fontSize: '0.75rem',
          color: '#64748b',
          padding: '0.5rem 0',
          borderTop: '1px solid #f1f5f9',
          fontStyle: 'italic',
        }}
      >
        Snapshot collected: {fmtDate(overview.snapshot_date)} · Units consumed:{' '}
        {fmtNum(overview.units_consumed)} / 100,000 ·{' '}
        {overview.units_consumed != null
          ? `${((overview.units_consumed / 100000) * 100).toFixed(2)}%`
          : '—'}{' '}
        · Next auto-run: tomorrow 02:00 UTC
      </div>
    </div>
  );
};

export default AhrefsOverviewTab;
